import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Pause, Play, RotateCcw } from 'lucide-react';
import { getSessionWaveform } from '../../api/sessions';
import { SkeletonBlock } from '../../components/SkeletonBlock';
const FALLBACK_SAMPLE_RATE_HZ = 20;
const VISIBLE_WINDOW_SECONDS = 10;
const MAX_DECODED_SAMPLES = 240_000;
const WAVEFORM_PAGE_LIMIT = 500;
const MAX_PAGE_REQUESTS = 40;
const GAP_TOLERANCE_FACTOR = 1.8;
interface WaveformSample {
  timestamp: number;
  motherValue: number | null;
  babyValue: number | null;
  segmentIndex: number;
}
interface DecodedWaveform {
  samples: WaveformSample[];
  rateHz: number;
  isTruncated: boolean;
  isPaginated: boolean;
}
interface SessionWaveformPlayerProps {
  token: string | null;
  sessionId?: string;
  startedAt?: string | null;
  endedAt?: string | null;
  isSessionLoading: boolean;
}

/**
 * Mirrors the backend `decodeToNumbers` helper: base64 -> little-endian int16
 * -> divided by the segment's scale factor.
 */
function decodeBase64Int16(encoded: unknown, scale: number): number[] {
  if (typeof encoded !== 'string' || encoded.length === 0) return [];
  let binary: string;
  try {
    binary = atob(encoded);
  } catch {
    return [];
  }
  const sampleCount = binary.length >> 1;
  if (sampleCount === 0) return [];
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const view = new DataView(bytes.buffer);
  const divisor = Number.isFinite(scale) && scale !== 0 ? scale : 1;
  const values = new Array<number>(sampleCount);
  for (let index = 0; index < sampleCount; index += 1) {
    values[index] = view.getInt16(index * 2, true) / divisor;
  }
  return values;
}
function pickNumber(source: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}
function pickString(source: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}
function pickTimestamp(source: Record<string, unknown>, keys: string[]): number | null {
  const raw = pickString(source, keys);
  if (raw) {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return parsed;
  }
  const numeric = pickNumber(source, keys);
  if (numeric !== null) return numeric < 1e12 ? numeric * 1000 : numeric;
  return null;
}
function normalizeSegmentList(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return response as Record<string, unknown>[];
  if (!response || typeof response !== 'object') return [];
  const container = response as Record<string, unknown>;
  for (const key of ['segments', 'waveform', 'data', 'items', 'results']) {
    const value = container[key];
    if (Array.isArray(value)) return value as Record<string, unknown>[];
  }
  return [];
}

/**
 * The endpoint's pagination support is unknown, so it is inferred from the
 * response rather than assumed: only explicit metadata makes us request a
 * second page.
 */
function readPaginationHint(response: unknown, returnedCount: number): {
  isPaginated: boolean;
  hasMore: boolean;
} {
  if (!response || typeof response !== 'object' || Array.isArray(response)) {
    return {
      isPaginated: false,
      hasMore: false
    };
  }
  const container = response as Record<string, unknown>;
  const meta = container.pagination && typeof container.pagination === 'object' ? container.pagination as Record<string, unknown> : container;
  const total = pickNumber(meta, ['total', 'totalCount', 'count']);
  const nextOffset = pickNumber(meta, ['nextOffset', 'next_offset']);
  const offset = pickNumber(meta, ['offset']);
  const explicitHasMore = typeof meta.hasMore === 'boolean' ? meta.hasMore : typeof meta.has_more === 'boolean' ? meta.has_more as boolean : null;
  const isPaginated = explicitHasMore !== null || total !== null || nextOffset !== null || offset !== null;
  if (!isPaginated) return {
    isPaginated: false,
    hasMore: false
  };
  if (explicitHasMore !== null) return {
    isPaginated: true,
    hasMore: explicitHasMore
  };
  if (nextOffset !== null) return {
    isPaginated: true,
    hasMore: returnedCount > 0
  };
  if (total !== null && offset !== null) {
    return {
      isPaginated: true,
      hasMore: offset + returnedCount < total
    };
  }
  return {
    isPaginated: true,
    hasMore: returnedCount >= WAVEFORM_PAGE_LIMIT
  };
}
function decodeSegments(segments: Record<string, unknown>[]): DecodedWaveform {
  const samples: WaveformSample[] = [];
  let rateHz = 0;
  let isTruncated = false;
  const ordered = [...segments].sort((a, b) => {
    const aStart = pickTimestamp(a, ['startedAt', 'startTime', 'from', 'ts']) ?? 0;
    const bStart = pickTimestamp(b, ['startedAt', 'startTime', 'from', 'ts']) ?? 0;
    return aStart - bStart;
  });
  ordered.forEach((segment, segmentIndex) => {
    if (isTruncated) return;
    const segmentStart = pickTimestamp(segment, ['startedAt', 'startTime', 'from', 'ts', 'recordedAt']);
    if (segmentStart === null) return;
    const segmentRate = pickNumber(segment, ['rateHz', 'sampleRateHz', 'sampleRate', 'rate']) ?? FALLBACK_SAMPLE_RATE_HZ;
    const scale = pickNumber(segment, ['scale', 'scaleFactor', 'scale_factor']) ?? 1;
    if (segmentRate > rateHz) rateHz = segmentRate;
    const motherValues = decodeBase64Int16(pickString(segment, ['motherWave', 'mother_wave', 'motherSamples']), scale);
    const babyValues = decodeBase64Int16(pickString(segment, ['babyWave', 'baby_wave', 'babySamples']), scale);
    const sampleCount = Math.max(motherValues.length, babyValues.length);
    if (sampleCount === 0) return;
    const intervalMs = 1000 / (segmentRate || FALLBACK_SAMPLE_RATE_HZ);
    for (let index = 0; index < sampleCount; index += 1) {
      if (samples.length >= MAX_DECODED_SAMPLES) {
        isTruncated = true;
        return;
      }
      samples.push({
        timestamp: segmentStart + index * intervalMs,
        motherValue: index < motherValues.length ? motherValues[index] : null,
        babyValue: index < babyValues.length ? babyValues[index] : null,
        segmentIndex
      });
    }
  });
  samples.sort((a, b) => a.timestamp - b.timestamp);
  return {
    samples,
    rateHz: rateHz || FALLBACK_SAMPLE_RATE_HZ,
    isTruncated,
    isPaginated: false
  };
}
function formatClock(milliseconds: number): string {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '0:00';
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  const paddedSeconds = String(seconds).padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
}
export function SessionWaveformPlayer({
  token,
  sessionId,
  startedAt,
  endedAt,
  isSessionLoading
}: SessionWaveformPlayerProps) {
  const [samples, setSamples] = useState<WaveformSample[]>([]);
  const [rateHz, setRateHz] = useState(FALLBACK_SAMPLE_RATE_HZ);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isPaginated, setIsPaginated] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cursorIndex, setCursorIndex] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cursorRef = useRef(0);
  const publishedCursorRef = useRef(0);
  const isPlayingRef = useRef(false);
  const samplesRef = useRef<WaveformSample[]>([]);
  const rateRef = useRef(FALLBACK_SAMPLE_RATE_HZ);
  useEffect(() => {
    samplesRef.current = samples;
  }, [samples]);
  useEffect(() => {
    rateRef.current = rateHz;
  }, [rateHz]);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    if (isSessionLoading) return;
    if (!token || !sessionId || !startedAt) {
      // Session loaded but has no usable time range — nothing to request.
      setSamples([]);
      setIsLoading(false);
      return;
    }
    let isActive = true;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const from = new Date(startedAt).toISOString();
        const to = new Date(endedAt || Date.now()).toISOString();
        const collected: Record<string, unknown>[] = [];
        let offset = 0;
        let paginated = false;
        let truncatedByPageCap = false;
        for (let request = 0; request < MAX_PAGE_REQUESTS; request += 1) {
          const response = await getSessionWaveform(token, sessionId, {
            from,
            to,
            limit: WAVEFORM_PAGE_LIMIT,
            offset
          });
          const pageSegments = normalizeSegmentList(response);
          collected.push(...pageSegments);
          const hint = readPaginationHint(response, pageSegments.length);
          paginated = hint.isPaginated;
          if (!hint.isPaginated || !hint.hasMore || pageSegments.length === 0) break;
          offset += pageSegments.length;
          if (request === MAX_PAGE_REQUESTS - 1) truncatedByPageCap = true;
        }
        if (!isActive) return;
        const decoded = decodeSegments(collected);
        setSamples(decoded.samples);
        setRateHz(decoded.rateHz);
        setIsTruncated(decoded.isTruncated || truncatedByPageCap);
        setIsPaginated(paginated);
        cursorRef.current = 0;
        publishedCursorRef.current = 0;
        setCursorIndex(0);
        setIsPlaying(false);
      } catch (err) {
        if (isActive) setError('Could not load waveform for this session.');
      } finally {
        if (isActive) setIsLoading(false);
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [endedAt, isSessionLoading, sessionId, startedAt, token]);
  const totalDurationMs = useMemo(() => {
    if (samples.length < 2) return 0;
    return samples[samples.length - 1].timestamp - samples[0].timestamp;
  }, [samples]);
  const elapsedMs = useMemo(() => {
    if (samples.length === 0) return 0;
    const index = Math.min(Math.floor(cursorIndex), samples.length - 1);
    return samples[index].timestamp - samples[0].timestamp;
  }, [cursorIndex, samples]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    let animationFrame = 0;
    let previousFrameTime: number | null = null;
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      const dpr = window.devicePixelRatio || 1;
      if (!parent) return dpr;
      const width = Math.max(1, Math.floor(parent.clientWidth * dpr));
      const height = Math.max(1, Math.floor(parent.clientHeight * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      return dpr;
    };
    const drawTrace = (windowSamples: WaveformSample[], accessor: (sample: WaveformSample) => number | null, width: number, height: number, color: string, glowColor: string, expectedIntervalMs: number, visibleCapacity: number) => {
      const values = windowSamples.map(accessor).filter((value): value is number => value !== null);
      if (values.length < 2) return;
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      const observedRange = maxValue - minValue;
      const visualRange = Math.max(observedRange * 1.25, Number.EPSILON);
      const baseline = minValue - (visualRange - observedRange) / 2;
      const paddingY = height * 0.12;
      const usableHeight = height - paddingY * 2;
      const step = width / Math.max(visibleCapacity - 1, 1);
      const startX = width - (windowSamples.length - 1) * step;
      const mapY = (value: number) => height - paddingY - (value - baseline) / visualRange * usableHeight;
      const paint = (strokeStyle: string, lineWidth: number) => {
        context.beginPath();
        let penDown = false;
        for (let index = 0; index < windowSamples.length; index += 1) {
          const sample = windowSamples[index];
          const value = accessor(sample);
          const previous = index > 0 ? windowSamples[index - 1] : null;
          // Bulk-mode rotation and delayed backfill leave real holes in the
          // record; break the stroke instead of drawing across them.
          const isGap = previous !== null && (previous.segmentIndex !== sample.segmentIndex || sample.timestamp - previous.timestamp > expectedIntervalMs * GAP_TOLERANCE_FACTOR);
          if (value === null) {
            penDown = false;
            continue;
          }
          const x = startX + index * step;
          const y = mapY(value);
          if (!penDown || isGap) {
            context.moveTo(x, y);
            penDown = true;
          } else {
            context.lineTo(x, y);
          }
        }
        context.strokeStyle = strokeStyle;
        context.lineWidth = lineWidth;
        context.lineJoin = 'round';
        context.lineCap = 'round';
        context.stroke();
      };
      paint(glowColor, 6);
      paint(color, 1.5);
    };
    const draw = (frameTime: number) => {
      const dpr = resizeCanvas();
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      const currentSamples = samplesRef.current;
      const currentRate = rateRef.current || FALLBACK_SAMPLE_RATE_HZ;
      const elapsedSeconds = previousFrameTime === null ? 0 : (frameTime - previousFrameTime) / 1000;
      previousFrameTime = frameTime;
      if (isPlayingRef.current && currentSamples.length > 1) {
        const nextCursor = cursorRef.current + elapsedSeconds * currentRate;
        const reachedEnd = nextCursor >= currentSamples.length - 1;
        cursorRef.current = reachedEnd ? currentSamples.length - 1 : nextCursor;
        if (reachedEnd) {
          isPlayingRef.current = false;
          setIsPlaying(false);
        }
        // The canvas reads the ref every frame; React state only drives the
        // seek bar and clock, so publish it a few times per second.
        const shouldPublish = reachedEnd || Math.abs(cursorRef.current - publishedCursorRef.current) >= currentRate / 5;
        if (shouldPublish) {
          publishedCursorRef.current = cursorRef.current;
          setCursorIndex(cursorRef.current);
        }
      }
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.save();
      context.scale(dpr, dpr);
      context.fillStyle = '#1A1211';
      context.fillRect(0, 0, width, height);
      context.strokeStyle = 'rgba(255,255,255,0.04)';
      context.lineWidth = 1;
      context.beginPath();
      for (let index = 1; index < 5; index += 1) {
        const y = height / 5 * index;
        context.moveTo(0, y);
        context.lineTo(width, y);
      }
      context.stroke();
      if (currentSamples.length > 1) {
        const visibleCapacity = Math.max(2, Math.round(currentRate * VISIBLE_WINDOW_SECONDS));
        const endIndex = Math.min(currentSamples.length - 1, Math.max(0, Math.floor(cursorRef.current)));
        const startIndex = Math.max(0, endIndex - visibleCapacity + 1);
        const windowSamples = currentSamples.slice(startIndex, endIndex + 1);
        const expectedIntervalMs = 1000 / currentRate;
        drawTrace(windowSamples, sample => sample.babyValue, width, height, '#6B21E8', 'rgba(107, 33, 232, 0.16)', expectedIntervalMs, visibleCapacity);
        drawTrace(windowSamples, sample => sample.motherValue, width, height, '#00D4D4', 'rgba(0, 212, 212, 0.16)', expectedIntervalMs, visibleCapacity);
      }
      context.restore();
      animationFrame = requestAnimationFrame(draw);
    };
    animationFrame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animationFrame);
  }, []);
  const handleSeek = useCallback((value: number) => {
    cursorRef.current = value;
    publishedCursorRef.current = value;
    setCursorIndex(value);
  }, []);
  const handleTogglePlay = useCallback(() => {
    if (samplesRef.current.length < 2) return;
    setIsPlaying(playing => {
      const next = !playing;
      if (next && cursorRef.current >= samplesRef.current.length - 1) {
        cursorRef.current = 0;
        publishedCursorRef.current = 0;
        setCursorIndex(0);
      }
      return next;
    });
  }, []);
  const handleRestart = useCallback(() => {
    cursorRef.current = 0;
    publishedCursorRef.current = 0;
    setCursorIndex(0);
  }, []);
  const showLoading = isSessionLoading || isLoading;
  const hasSamples = samples.length > 1;
  return <section className="bg-white rounded-[20px] shadow-sm p-4 md:p-5" aria-labelledby="session-waveform-heading" data-id="element-1191" data-mp="32a8v7wexkc3q">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4" data-id="element-1192" data-mp="2t8rh2jl32kho">
        <div data-id="element-1193" data-mp="2tavovcm3s3n8">
          <h2 id="session-waveform-heading" className="text-[16px] font-semibold text-text-primary" data-id="element-1194" data-mp="2y7ucrdf6becz">
            Waveform Playback
          </h2>
          <p className="text-[13px] text-text-secondary mt-0.5" data-id="element-1195" data-mp="1hvp4dzm3uf2s">
            Recorded fetal and maternal traces from this session
          </p>
        </div>
        {hasSamples && <div className="flex items-center gap-4" data-id="element-1196" data-mp="2dk3o62v18mm9">
            <span className="flex items-center gap-1.5 text-[12px] text-text-secondary" data-id="element-1197" data-mp="29gnrx7mvkoa3">
              <span className="w-[14px] h-[2px] rounded-[1px] bg-primary" data-id="element-1198" data-mp="24hubp7g7ej68" />
              Fetal
            </span>
            <span className="flex items-center gap-1.5 text-[12px] text-text-secondary" data-id="element-1199" data-mp="1zjsvcua5wxyg">
              <span className="w-[14px] h-[2px] rounded-[1px] bg-accent" data-id="element-1200" data-mp="2x79pu6360s6d" />
              Maternal
            </span>
          </div>}
      </div>

      {showLoading ? <div data-id="element-1201" data-mp="2ngyl4hbnqeyf">
          <SkeletonBlock height="200px" borderRadius="16px" data-id="element-1202" data-mp="2ngoucrisg3ro" />
          <p className="text-[13px] text-text-secondary mt-3" data-id="element-1203" data-mp="2nf3vwqtcs0oj">
            Loading and decoding waveform segments…
          </p>
        </div> : error ? <div className="bg-danger/10 border border-danger/20 rounded-[16px] p-4 flex items-start gap-3" data-id="element-1204" data-mp="3fyaxdiicpw1j">
          <AlertTriangle size={18} className="text-danger flex-shrink-0 mt-0.5" data-id="element-1205" data-mp="3b5hqop3bodmt" />
          <p className="text-[14px] text-text-primary" data-id="element-1206" data-mp="3iih23seshqj5">{error}</p>
        </div> : !hasSamples ? <div className="bg-secondary rounded-[16px] p-6 text-center" data-id="element-1207" data-mp="3nfz3mxqh93h4">
          <p className="text-[14px] text-text-primary font-medium mb-1" data-id="element-1208" data-mp="3sg4wuiceay1b">
            No waveform data available for this session
          </p>
          <p className="text-[13px] text-text-secondary max-w-[360px] mx-auto" data-id="element-1209" data-mp="1abtln9p4jk0">
            This session was recorded before waveform segments were stored, or no
            segments reached the server.
          </p>
        </div> : <>
          <div className="w-full h-[200px] md:h-[240px] rounded-[16px] overflow-hidden relative bg-[#1A1211]" data-id="element-1210" data-mp="f18ltczpmyqj">
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label={`Recorded fetal and maternal waveform, played back at ${rateHz} samples per second`} data-id="element-1211" data-mp="k0l7vvirc1s2" />
          </div>

          <div className="mt-4 flex items-center gap-3" data-id="element-1212" data-mp="sydm0wcqt3dv">
            <button type="button" onClick={handleTogglePlay} className="w-[40px] h-[40px] rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 hover:bg-primary/90 transition-colors" aria-label={isPlaying ? 'Pause waveform playback' : 'Play waveform playback'} data-id="element-1213" data-mp="1si58u8qs2zqz">
              {isPlaying ? <Pause size={18} data-id="element-1214" data-mp="2bghli4gwerrb" /> : <Play size={18} data-id="element-1215" data-mp="2bewpsbiul2rv" />}
            </button>
            <button type="button" onClick={handleRestart} className="w-[40px] h-[40px] rounded-full border border-border text-text-secondary flex items-center justify-center flex-shrink-0 hover:bg-secondary transition-colors" aria-label="Restart waveform playback" data-id="element-1216" data-mp="2gesukczdcy8h">
              <RotateCcw size={16} data-id="element-1217" data-mp="2u78rqx5la21h" />
            </button>

            <input type="range" min={0} max={Math.max(1, samples.length - 1)} step={1} value={Math.floor(cursorIndex)} onChange={event => handleSeek(Number(event.target.value))} className="flex-1 h-[4px] accent-primary cursor-pointer" aria-label="Seek within session waveform" data-id="element-1218" data-mp="2z5a9nera3vbd" />

            <span className="text-[13px] tabular-nums text-text-secondary flex-shrink-0" data-id="element-1219" data-mp="9i5t8wdum98b">
              {formatClock(elapsedMs)} / {formatClock(totalDurationMs)}
            </span>
          </div>

          <p className="text-[12px] text-text-secondary mt-2" data-id="element-1220" data-mp="126teirylkylv">
            {rateHz} Hz · {samples.length.toLocaleString()} samples
          </p>

          {isTruncated && <div className="mt-3 bg-amber-500/10 border border-amber-500/20 rounded-[12px] p-3 flex items-start gap-2" data-id="element-1221" data-mp="xe0a6571jsab">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1222" data-mp="1pwff9jumblvz" />
              <p className="text-[13px] text-amber-700" data-id="element-1223" data-mp="1l4nx9xpj2ohg">
                This session is longer than the playback buffer. Showing the
                earliest {formatClock(totalDurationMs)} of recorded waveform
                {isPaginated ? '.' : '; the endpoint returned no pagination metadata.'}
              </p>
            </div>}
        </>}
    </section>;
}